import { ChangeDetectionStrategy, Component, HostBinding, Input, ViewEncapsulation } from '@angular/core';

/// Tipo de dato de la celda: decide cómo se pinta y se alinea.
export type TableCellType = 'texto' | 'ref' | 'fecha' | 'importe' | 'unidad' | 'porcentaje' | 'bool';

/// Celda de tabla con el estilo de su tipo de dato. Es el propio `<td>` (selector de atributo), así que no añade
/// ningún elemento entre `<tr>` y `<td>` y la tabla conserva anchos, bordes y la cabecera clonada de
/// appStickyHscroll.
///
///     <td appCell="importe" [value]="row.autorizado"></td>
///     <td appCell="unidad" [value]="row.duracionObra" unit="días"></td>
///
/// Solo presentación: el valor se recibe ya resuelto y no se transforma, de modo que ordenación, filtros y
/// exportación siguen trabajando sobre el dato original. Lo que es propio de una pantalla (los colores por estado,
/// por ejemplo) se queda en esa pantalla, con un `<td>` normal.
@Component({
    selector: 'td[appCell]',
    templateUrl: './table-cell.component.html',
    styleUrls: ['./table-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Sin encapsulación: las reglas del <td> tienen que pisar las de PrimeNG (`.p-datatable ... > td`)
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TableCellComponent {
  @Input({ alias: 'appCell', required: true }) public type!: TableCellType;
  @Input() public value: string | number | boolean | Date | null | undefined = null;

  /// Unidad que sigue a la cifra en el tipo `unidad` (`días`, `m²`…).
  @Input() public unit: string = '';

  /// Tooltip explícito. Si no se da, `texto` muestra el valor completo.
  @Input() public tooltip: string | null = null;

  /// Tope de ancho en px del contenido de la celda, para cualquier tipo. Lo que no cabe acaba en "…" (en `texto`,
  /// tras dos líneas), con el valor completo en el tooltip si es `texto`. Sin él, solo `texto` tiene tope (300px).
  @Input() public maxWidth: number | null = null;

  @HostBinding('class')
  public get hostClass(): string {
    return `cell cell--${this.type}`;
  }

  @HostBinding('attr.title')
  public get hostTitle(): string | null {
    if (this.tooltip != null) return this.tooltip;
    if (this.type === 'texto' && !this.isEmpty) return String(this.value);
    return null;
  }

  public get isEmpty(): boolean {
    return this.value == null || this.value === '';
  }

  /// El valor como número. Acepta también texto numérico (`"12.50"`), por si una vista o un endpoint devuelve el
  /// decimal como cadena: sin esto la celda de importe saldría solo con el `€`.
  public get numberValue(): number | null {
    if (typeof this.value === 'number') return this.value;
    if (typeof this.value === 'string' && this.value.trim() !== '') {
      const n = Number(this.value);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  public get dateValue(): Date | string | null {
    return this.value instanceof Date || typeof this.value === 'string' ? this.value : null;
  }

  /// Sí/No: acepta booleanos y los literales que devuelven las vistas (`VERDADERO`/`FALSO`). Cualquier otro valor
  /// devuelve null y se pinta tal cual.
  public get boolValue(): boolean | null {
    if (typeof this.value === 'boolean') return this.value;
    switch (String(this.value).trim().toUpperCase()) {
      case 'VERDADERO': case 'TRUE': return true;
      case 'FALSO': case 'FALSE': return false;
      default: return null;
    }
  }
}
